import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download01, Key01, Play } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppSelect } from "@/components/app/AppSelect";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import {
  downloadScenarioExport,
  issueScenarioApiKey,
  runScenario,
  useApiKeyTtls,
  useCatalogProblems,
  useExportSystems,
  useScenarioDetail,
  useScenarios,
  type ScenarioEvent,
  type ScenarioStatus,
  type IssuedKey,
  type ScenarioSummary,
} from "@/lib/scenarios";
import { Field, inputCls } from "./shared";
import { formatDateTime, formatTime } from "@/lib/format";

function statusColor(status: ScenarioStatus): "success" | "error" | "gray" {
  if (status === "AVAILABLE") return "success";
  if (status === "INVALID") return "error";
  return "gray";
}

export function ScenariosPage() {
  const { t } = useTranslation();
  const scenarios = useScenarios();
  const systems = useExportSystems();
  const problems = useCatalogProblems();
  const ttls = useApiKeyTtls();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [system, setSystem] = useState("jmeter");
  const [mode, setMode] = useState("FUNCTIONAL");
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [events, setEvents] = useState<ScenarioEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [ttl, setTtl] = useState("DAY");
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const detail = useScenarioDetail(selectedId);
  const selected = useMemo(
    () => scenarios.data?.find((s) => s.id === selectedId) ?? null,
    [scenarios.data, selectedId],
  );

  /** Scénáře seskupené podle prvního tagu — plochý seznam dvaceti je nepoužitelný. */
  const groups = useMemo(() => {
    const byTag = new Map<string, ScenarioSummary[]>();
    for (const scenario of scenarios.data ?? []) {
      const tag = scenario.tags[0] ?? "other";
      byTag.set(tag, [...(byTag.get(tag) ?? []), scenario]);
    }
    return [...byTag.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [scenarios.data]);

  const modesForSystem = systems.data?.find((s) => s.id === system)?.modes ?? ["FUNCTIONAL"];

  function select(scenario: ScenarioSummary) {
    setSelectedId(scenario.id);
    setInputs({});
    setEvents([]);
    setMsg(null);
    setIssued(null);
  }

  /**
   * Vystaví klíč a rovnou ho dosadí do vstupu, takže po kliknutí jde scénář spustit.
   * Oprávnění se neptáme uživatele — odvodí se z endpointů, které scénář volá.
   */
  async function issueKey() {
    if (!selectedId) return;
    setBusy(true);
    setMsg(null);
    const result = await issueScenarioApiKey(selectedId, ttl);
    setBusy(false);
    if (result.ok) {
      setIssued(result.key);
      setInputs((previous) => ({ ...previous, apiKey: result.key.value }));
    } else {
      setMsg({ ok: false, text: result.error });
    }
  }

  async function run() {
    if (!selectedId) return;
    setBusy(true);
    setEvents([]);
    setMsg(null);
    const result = await runScenario(selectedId, inputs, (event) =>
      setEvents((previous) => [...previous, event]),
    );
    setBusy(false);
    if (!result.ok) setMsg({ ok: false, text: result.error ?? "" });
  }

  async function exportBundle() {
    if (!selectedId) return;
    setBusy(true);
    setMsg(null);
    const result = await downloadScenarioExport(selectedId, system, mode);
    setBusy(false);
    setMsg(
      result.ok
        ? { ok: true, text: t("scenarios.exported") }
        : { ok: false, text: result.error ?? "" },
    );
  }

  if (scenarios.isLoading) return <LoadingIndicator />;
  if (scenarios.isError) return <div className="text-error-primary">{t("common.loadError")}</div>;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("scenarios.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("scenarios.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {(problems.data?.length ?? 0) > 0 && (
        <div className="rounded-lg bg-secondary px-3 py-2 text-sm ring-1 ring-error">
          <span className="text-error-primary">{t("scenarios.documentProblems")}</span>
          <ul className="mt-1 list-disc pl-5 text-secondary">
            {problems.data?.map((problem, index) => (
              <li key={index}>
                {problem.message} <span className="text-tertiary">({problem.origin})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <h2 className="text-md font-semibold text-primary">{t("scenarios.catalog")}</h2>
          {groups.map(([tag, items]) => (
            <div key={tag} className="flex flex-col gap-2">
              <div className="text-xs font-semibold uppercase text-tertiary">{tag}</div>
              {items.map((scenario) => (
                <button
                  key={scenario.id}
                  type="button"
                  onClick={() => select(scenario)}
                  className={`flex flex-col gap-1 rounded-lg px-3 py-2 text-left ring-1 ${
                    scenario.id === selectedId ? "bg-secondary ring-brand" : "ring-secondary"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-primary">{scenario.title}</span>
                    <Badge type="pill-color" size="sm" color={statusColor(scenario.status)}>
                      {scenario.status}
                    </Badge>
                    {!scenario.loadSafe && (
                      <Badge type="pill-color" size="sm" color="warning">
                        {t("scenarios.notLoadSafe")}
                      </Badge>
                    )}
                  </div>
                  {/* Proč scénář nejde použít, se ukazuje rovnou. Skrytý scénář je
                      nezodpověditelný dotaz na podporu. */}
                  {scenario.unavailableReasons.length > 0 && (
                    <span className="text-xs text-tertiary">
                      {scenario.unavailableReasons.join("; ")}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          {!selected && (
            <div className="rounded-xl bg-primary p-5 text-sm text-tertiary ring-1 ring-secondary">
              {t("scenarios.pickOne")}
            </div>
          )}

          {selected && (
            <>
              <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
                <h2 className="text-md font-semibold text-primary">{selected.title}</h2>
                {selected.description && (
                  <p className="text-sm text-secondary">{selected.description}</p>
                )}
                <div className="text-xs text-tertiary">
                  {selected.id} · {t("scenarios.steps", { count: selected.stepCount })}
                </div>

                {(detail.data?.scenario.inputs ?? []).map((input) => (
                  <Field key={input.name} label={input.name + (input.required ? " *" : "")}>
                    <input
                      className={inputCls}
                      type={input.secret ? "password" : "text"}
                      value={inputs[input.name] ?? input.defaultValue ?? ""}
                      onChange={(e) =>
                        setInputs((previous) => ({ ...previous, [input.name]: e.target.value }))
                      }
                    />
                  </Field>
                ))}

                <div className="flex flex-wrap items-end gap-3 border-t border-secondary pt-4">
                  <Field label={t("scenarios.keyTtl")}>
                    <AppSelect value={ttl} onChange={(e) => setTtl(e.target.value)}>
                      {ttls.data?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </AppSelect>
                  </Field>
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={Key01}
                    isDisabled={busy || selected.status !== "AVAILABLE"}
                    onPress={() => void issueKey()}
                  >
                    {t("scenarios.issueKey")}
                  </Button>
                  {issued && (
                    <div className="w-full rounded-lg bg-secondary px-3 py-2 text-sm ring-1 ring-secondary">
                      <div className="text-secondary">
                        {t("scenarios.keyIssued", {
                          scopes: issued.scopes.join(", "),
                          expires: formatDateTime(issued.expiresAt),
                        })}
                      </div>
                      {/* Hodnota se zobrazí jen teď — server ji v čitelné podobě neuchovává. */}
                      <code className="mt-1 block break-all text-xs text-primary">{issued.value}</code>
                      {issued.unresolved.length > 0 && (
                        <div className="mt-1 text-xs text-warning-primary">
                          {t("scenarios.keyUnresolved", { steps: issued.unresolved.join(", ") })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <Field label={t("scenarios.system")}>
                    <AppSelect
                      value={system}
                      onChange={(e) => {
                        setSystem(e.target.value);
                        const next =
                          systems.data?.find((s) => s.id === e.target.value)?.modes ?? [];
                        if (!next.includes(mode)) setMode(next[0] ?? "FUNCTIONAL");
                      }}
                    >
                      {systems.data?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.displayName}
                        </option>
                      ))}
                    </AppSelect>
                  </Field>
                  <Field label={t("scenarios.mode")}>
                    {/* Nabízí se jen to, co zvolený nástroj umí vyjádřit — SoapUI
                        například zátěžový režim nemá. */}
                    <AppSelect value={mode} onChange={(e) => setMode(e.target.value)}>
                      {modesForSystem.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </AppSelect>
                  </Field>
                  <Button
                    size="md"
                    iconLeading={Download01}
                    isDisabled={busy || selected.status === "INVALID"}
                    onPress={() => void exportBundle()}
                  >
                    {t("scenarios.export")}
                  </Button>
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={Play}
                    isDisabled={busy || selected.status !== "AVAILABLE"}
                    onPress={() => void run()}
                  >
                    {busy ? t("testing.running") : t("scenarios.run")}
                  </Button>
                </div>
              </div>

              {msg && (
                <div
                  className={`rounded-lg px-3 py-2 text-sm ring-1 ${
                    msg.ok
                      ? "bg-secondary ring-secondary text-success-primary"
                      : "bg-secondary ring-error text-error-primary"
                  }`}
                >
                  {msg.text}
                </div>
              )}

              {events.length > 0 && (
                <div className="flex flex-col gap-2 rounded-xl bg-primary p-5 ring-1 ring-secondary">
                  <h2 className="text-md font-semibold text-primary">{t("testing.progress")}</h2>
                  {events.map((event, index) => (
                    <div key={index} className="flex flex-col gap-1 text-sm">
                      <div className="flex items-center gap-3">
                        <Badge
                          type="pill-color"
                          size="sm"
                          color={
                            event.kind === "FAILED"
                              ? "error"
                              : event.kind === "COMPLETED"
                                ? "success"
                                : "blue"
                          }
                        >
                          {event.kind}
                        </Badge>
                        <span className="tabular-nums text-tertiary">
                          {formatTime(event.timestamp)}
                        </span>
                        {event.stepTitle && <span className="text-secondary">{event.stepTitle}</span>}
                        {event.outcome && (
                          <span className="tabular-nums text-tertiary">
                            {event.outcome.httpStatus} · {event.outcome.durationMs} ms
                          </span>
                        )}
                      </div>
                      {event.outcome?.assertions
                        ?.filter((assertion) => !assertion.passed)
                        .map((assertion, i) => (
                          <span key={i} className="pl-6 text-xs text-error-primary">
                            {assertion.description} — {assertion.detail}
                          </span>
                        ))}
                      {event.error && (
                        <span className="pl-6 text-xs text-error-primary">{event.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
